import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DateRangePicker.css';
import { CalendarRange, ChevronDown } from 'lucide-react';
import IconButton from '@/ctComponents/IconButton';
import CloseButton from '@/ctComponents/closeButton';
import SubmitButton from '@/ctComponents/submitButton';

const RANGE_ITEMS = [
  {
    label: '1 Hour',
    value: 'HOUR',
  },
  {
    label: '4 Hours',
    value: 'HOUR4',
  },
  {
    label: '6 Hours',
    value: 'HOUR6',
  },
  {
    label: '12 Hours',
    value: 'HOUR12',
  },
  {
    label: '1 Day',
    value: 'DAY',
  },
  {
    label: '1 Week',
    value: 'WEEK',
  },
  {
    label: '1 Month',
    value: 'MONTH',
  },
];

const DateRangePicker = ({
  dateRangePicker,
  setDateRangePicker,
  dateRangePickerRef,
  handleDateRangeClick,
  handleDateRangeChange,
  handleFilterTypeChange,
  handleDateRangeReset,
  handleDateRangeApply,
  formatDateRangeShort,
  formatDateForInput,
  formatDateForInputTo,
}) => {
  return (
    <div className="relative" ref={dateRangePickerRef}>
      <IconButton
        active={dateRangePicker.open}
        text={
          <div
            className={`flex items-center gap-2 ${
              dateRangePicker.startDate && dateRangePicker.endDate
                ? 'text-primary-text font-bold'
                : 'text-inactive-text font-normal'
            }`}>
            <CalendarRange size={16} />
            <span className="whitespace-nowrap">
              {formatDateRangeShort(
                dateRangePicker.startDate,
                dateRangePicker.endDate
              )}
            </span>
            <ChevronDown size={16} />
          </div>
        }
        color="#131517"
        onClick={handleDateRangeClick}
        style={{
          height: 40,
        }}
      />

      {dateRangePicker.open && (
        <div className="absolute top-full right-0 mt-2 bg-white border border-solid border-light-gray-border rounded shadow-date-picker-range z-999 w-514 box-border p-3">
          <div className="flex gap-3">
            {/* Left Panel - Filter Options */}
            <div className="flex flex-col gap-2 w-[124px]">
              <div className="pb-4 border-b border-t-0 border-l-0 border-r-0 border-solid border-tertiary">
                <p className="text-sm m-0 font-averta-bold text-primary-text">
                  Time Step
                </p>
              </div>
              {RANGE_ITEMS.map((filterType) => (
                <button
                  key={filterType.value}
                  onClick={() => handleFilterTypeChange(filterType.value)}
                  className={`px-4 py-2 cursor-pointer rounded border border-solid text-sm text-center transition-all m-0 ${
                    (dateRangePicker.tempFilterType ||
                      dateRangePicker.filterType) === filterType.value
                      ? 'bg-white border-primary-blue text-primary-blue font-averta-bold'
                      : 'bg-white border-tertiary text-inactive-text font-averta'
                  }`}>
                  {filterType.label}
                </button>
              ))}
            </div>

            <div className="w-px bg-border-tertiary"></div>

            <div className="flex-1 flex flex-col gap-2 justify-between">
              <div className={`custom-report-datepicker-wrapper`}>
                <div className="pb-4 mb-2 border-b border-t-0 border-l-0 border-r-0 border-solid border-tertiary">
                  <p className="text-sm m-0 font-averta-bold text-primary-text">
                    Time Range
                  </p>
                </div>
                <DatePicker
                  selected={dateRangePicker.tempStartDate}
                  onChange={handleDateRangeChange}
                  startDate={dateRangePicker.tempStartDate}
                  endDate={dateRangePicker.tempEndDate}
                  selectsRange
                  inline
                  showPopperArrow={false}
                />
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-border-tertiary">
                <div className="flex items-center gap-2 relative">
                  {dateRangePicker.tempStartDate && (
                    <div className="bg-white border border-border-tertiary border-solid rounded h-8 px-2 flex items-center">
                      <p className="font-averta text-primary-text whitespace-nowrap text-11">
                        {dateRangePicker.tempStartDate
                          ? formatDateForInput(dateRangePicker.tempStartDate)
                          : 'From'}
                      </p>
                    </div>
                  )}
                  {dateRangePicker.tempStartDate &&
                    dateRangePicker.tempEndDate && (
                      <div className="w-1 h-px bg-primary-text"></div>
                    )}
                  {dateRangePicker.tempEndDate && (
                    <div className="bg-white border border-border-tertiary border-solid rounded h-8 px-2  flex items-center justify-center">
                      <p className="text-11 font-averta text-primary-text whitespace-nowrap">
                        {dateRangePicker.tempEndDate
                          ? formatDateForInputTo(dateRangePicker.tempEndDate)
                          : 'To'}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <CloseButton
                    color="#131517"
                    onClick={handleDateRangeReset}
                    text="Reset"
                    className="h-8"
                    width={64}
                  />
                  <SubmitButton
                    onClick={handleDateRangeApply}
                    text="Apply"
                    className="h-8 min-w-unset"
                    width={61}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
